import React, { useState } from 'react';
import {
	Group,
	useLazyMassFindQuery,
	User,
	WorkplaceMember,
} from '../../../../../graphql/graphql';
import { FieldError } from 'react-hook-form';
import MultiSearchWithCheckList, {
	MultiSearchWithCheckListValue,
} from '../MultiSearchWithCheckList/MultiSearchWithCheckList';
import { MultiSearchWithCheckListItemType } from '../../../Enums/MultiSearchWithCheckListItemType.enum';
import useTranslation from '../../../../../Hooks/useTranslation';

interface Props {
	onChange?: (value: MultiSearchWithCheckListValue) => void;
	workplaceId?: string;
	value?: MultiSearchWithCheckListValue;
	error?: FieldError;
	placeholder?: string;
	portal?: boolean;
	enableGroups?: boolean;
	hasError?: boolean;
}

const MessageRecipientsMultiSelectWithCheckList: React.FC<Props> = props => {
	const { value, onChange, portal, enableGroups = true, hasError } = props;
	const { t } = useTranslation();
	const [defaultValue, setDefaultValue] = useState<
		MultiSearchWithCheckListValue | undefined
	>(value);
	const [searchTimeout, setSearchTimeout] = useState<any>(undefined);
	const [isMorePatientsToggled, setIsMorePatientsToggled] = useState(false);
	const [getMassFind] = useLazyMassFindQuery();

	const handleSearch = (
		inputValue: string,
		callback: (options: MultiSearchWithCheckListValue[]) => void,
	) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const loadData = async () => {
			const items: MultiSearchWithCheckListValue[] = [];

			const searchResult = await getMassFind({ fulltext: inputValue });
			const doctors: WorkplaceMember[] = [];
			const nonDoctors: WorkplaceMember[] = [];
			const patients: WorkplaceMember[] = searchResult?.data?.workplacePatients
				?.data as WorkplaceMember[];
			const groups: Group[] = searchResult?.data?.groups?.data as Group[];

			if ((searchResult?.data?.workplaceDoctors?.data?.length || 0) > 0) {
				searchResult?.data?.workplaceDoctors?.data?.forEach(workplaceDoctor => {
					workplaceDoctor?.workplaceMemberRoles?.forEach(role => {
						if (
							role?.role?.key === 'doctor' &&
							!doctors.some(item => item.id === workplaceDoctor.id)
						) {
							doctors.push(workplaceDoctor as WorkplaceMember);
						}
						if (
							role?.role?.key !== 'doctor' &&
							!nonDoctors.some(item => item.id === workplaceDoctor.id)
						) {
							nonDoctors.push(workplaceDoctor as WorkplaceMember);
						}
					});
				});
			}

			//PATIENTS

			const haveFoundPatients = (patients?.length || 0) > 0;

			if (haveFoundPatients) {
				items.push({
					label:
						t('component.messageRecipientsMultiSelectWithCheckList.patients') ??
						'',
					value: '',
					type: MultiSearchWithCheckListItemType.ITEM_GROUP_TITLE,
				});

				patients.forEach(workplacePatient => {
					const item: User = workplacePatient?.user as User;
					if (item?.profile?.fullName && item?.id) {
						const userRelations: {
							workplaceName: string;
							relatedUserName: string;
						}[] = [];
						(item?.userRelations ?? []).forEach(relation => {
							if (
								relation?.workplace?.name &&
								relation?.relationUser?.profile?.fullName
							) {
								userRelations.push({
									workplaceName: relation?.workplace?.name,
									relatedUserName: relation?.relationUser?.profile?.fullName,
								});
							}
						});
						items.push({
							label: item.profile.fullName,
							value: item.id,
							type: MultiSearchWithCheckListItemType.PATIENT,
							itemData: {
								birthNumber: item?.profile?.birthNumber ?? undefined,
								age: item?.profile?.age ?? undefined,
								phoneNumber: item?.formattedPhoneNumber ?? undefined,
								address: item?.profile?.address?.fullAddress ?? undefined,
								userRelations,
							},
						});
					}
				});

				items.push({
					label: '',
					value: '',
					type: MultiSearchWithCheckListItemType.DIVIDER,
				});
			}

			//DOCTORS

			const haveFoundDoctors = (doctors?.length || 0) > 0;

			if (haveFoundDoctors) {
				items.push({
					label:
						t('component.messageRecipientsMultiSelectWithCheckList.doctors') ??
						'',
					value: '',
					type: MultiSearchWithCheckListItemType.ITEM_GROUP_TITLE,
				});
			}

			doctors.forEach(workplaceDoctor => {
				const item = workplaceDoctor.user;
				if (item?.profile?.fullName && item?.id) {
					items.push({
						label: item.profile.fullName,
						value: item.id,
						type: MultiSearchWithCheckListItemType.DOCTOR,
					});
				}
			});

			if (haveFoundDoctors) {
				items.push({
					label: '',
					value: '',
					type: MultiSearchWithCheckListItemType.DIVIDER,
				});
			}

			//OTHERS

			const haveFoundNonDoctors = (nonDoctors?.length || 0) > 0;

			if (haveFoundNonDoctors) {
				items.push({
					label:
						t(
							'component.messageRecipientsMultiSelectWithCheckList.otherHealthStaffAndNonHealthStaff',
						) ?? '',
					value: '',
					type: MultiSearchWithCheckListItemType.ITEM_GROUP_TITLE,
				});
			}

			nonDoctors.forEach(workplaceDoctor => {
				const item = workplaceDoctor.user;
				if (item?.profile?.fullName && item?.id) {
					items.push({
						label: item.profile.fullName,
						value: item.id,
						type: MultiSearchWithCheckListItemType.NON_DOCTOR,
					});
				}
			});

			if (haveFoundNonDoctors) {
				items.push({
					label: '',
					value: '',
					type: MultiSearchWithCheckListItemType.DIVIDER,
				});
			}

			//SKUPINY

			if (enableGroups) {
				const haveFoundGroups = (groups?.length || 0) > 0;

				if (haveFoundGroups) {
					items.push({
						label:
							t('component.messageRecipientsMultiSelectWithCheckList.groups') ??
							'',
						value: '',
						type: MultiSearchWithCheckListItemType.ITEM_GROUP_TITLE,
					});

					groups.forEach(item => {
						if (item?.name && item?.id) {
							items.push({
								label: item.name,
								value: item.id,
								type: MultiSearchWithCheckListItemType.GROUP,
							});
						}
					});
				}
			}

			callback(items);
		};

		setSearchTimeout(
			setTimeout(() => {
				loadData();
			}, 500),
		);
	};

	return (
		<MultiSearchWithCheckList
			{...props}
			onSearch={handleSearch}
			onChange={onChange}
			value={defaultValue?.label ? defaultValue : value}
			portal={portal}
		/>
	);

	return null;
};

export default MessageRecipientsMultiSelectWithCheckList;
